import React, { JSX, useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import {
  PlusIcon,
  TrashIcon,
  PencilSquareIcon,
  EyeIcon,
  DocumentDuplicateIcon,
  ArrowPathIcon,
  ArrowLeftIcon,
  QuestionMarkCircleIcon,
  LockClosedIcon,
  SparklesIcon,
  WrenchScrewdriverIcon,
  XCircleIcon
} from '@heroicons/react/24/outline'
import { LoadingSpinner } from 'components/Loading'
import { NumberInput } from 'components/NumberInput'
import { ColorInput, BackgroundInput } from 'components/ColorPickers'
import { ErrorInline } from 'components/ErrorInline'
import { SavedNote } from 'components/SavedNote'
import { ProviderBrandIcon, TWITCH_BRAND_ICON_SRC } from 'components/ProviderBrandIcon'
import { BackgroundImageInput } from 'components/BackgroundImageInput'
import { ScaledStage } from 'components/ScaledStage'
import { TextSizeInput } from 'components/TextSizeInput'
import { TWITCH_CHANNELS_OPTIONAL_HINT, eventHasYouTubeSource } from 'components/EventYouTubeSources'
import {
  TriviaDraft,
  TriviaLiveData,
  TriviaPreview,
  draftFromTrivia,
  triviaLiveFromSummary,
  triviaTextSizeDefaults
} from 'components/TriviaPreview'
import { TriviaLeaderboardPanel } from 'components/TriviaLeaderboardPanel'
import { POLL } from 'lib/elementCanvas'
import { TEXT_SIZE_MAX_PX, TEXT_SIZE_MIN_PX, usePreviewFit } from 'lib/textSizes'
import { elementDisplayName, sortByName } from 'lib/elementOrder'
import { ChatFontKey } from 'lib/chatFonts'
import { FontSelect } from 'components/FontSelect'
import {
  DEFAULT_TRIVIA_GRACE_SECONDS,
  TriviaStatusChip,
  triviaPhaseOf,
  triviaRevealArmed,
  triviaResetConfirmCopy,
  triviaYouTubeCounting,
  triviaYouTubeTimedOut,
  triviaRevealNowOffered,
  triviaCountingButtonLabel,
  triviaCountingTooltip,
  useTriviaGraceTick,
  TRIVIA_REVEAL_NOW_CONFIRM,
  TRIVIA_REVEAL_NOW_TOOLTIP,
  TRIVIA_STILL_COUNTING_LABEL,
  TRIVIA_STILL_COUNTING_TOOLTIP
} from 'lib/triviaPhase'
import { ConfirmDialog } from 'components/ConfirmDialog'
import { useAuth } from 'context/user'
import { useEntitlements } from 'context/plan'
import { useSocketToken } from 'context/socketToken'
import { useEventDetail } from 'hooks/useEventDetail'
import { useTriviaAdminFold } from 'hooks/useTriviaAdminFold'
import { graphqlRequest } from 'lib/helpers'
import {
  TriviaSummary,
  TriviaOptionInput,
  EventDetail,
  ChannelBinding,
  DeletionResult
} from 'types/api'
import createTriviaMutation from 'graphql/createTrivia.graphql'
import updateTriviaMutation from 'graphql/updateTrivia.graphql'
import deleteTriviaMutation from 'graphql/deleteTrivia.graphql'
import duplicateTriviaMutation from 'graphql/duplicateTrivia.graphql'
import closeTriviaMutation from 'graphql/closeTrivia.graphql'
import revealTriviaMutation from 'graphql/revealTrivia.graphql'
import stopTriviaMutation from 'graphql/stopTrivia.graphql'
import resetTriviaMutation from 'graphql/resetTrivia.graphql'

const SectionCard = ({
  title,
  children,
  action
}: { title: string, children: React.ReactNode, action?: React.ReactNode }): JSX.Element => (
  <section className='rounded-xl border border-white/5 bg-dm-bg-secondary p-5'>
    <div className='mb-4 flex items-center justify-between'>
      <h3 className='text-base font-semibold text-dm-text-primary'>{title}</h3>
      {action}
    </div>
    {children}
  </section>
)

const FixAnswerPanel = ({ trivia, busy, onApply, onCancel }: {
  trivia: TriviaSummary
  busy: boolean
  onApply: (index: number) => void
  onCancel: () => void
}): JSX.Element => {
  const current = trivia.options.findIndex((o) => o.isCorrect === true)
  const [picked, setPicked] = useState<number>(current)
  return (
    <div className='mt-3 rounded-md border border-white/10 bg-dm-bg-accent/20 p-3'>
      <div className='mb-2 text-xs font-medium uppercase tracking-wider text-dm-text-accent'>
        Fix the correct answer
      </div>
      <ul className='space-y-1.5'>
        {trivia.options.map((o, i) => (
          <li key={o.optionId}>
            <label className='flex cursor-pointer items-center gap-2 text-sm text-dm-text-primary'>
              <input
                type='radio'
                name={`fix-correct-${trivia.triviaId}`}
                checked={picked === i}
                onChange={() => setPicked(i)}
                className='size-4 border-white/20 bg-dm-bg-accent/60 text-emerald-500 focus:ring-emerald-500'
              />
              <span className='font-mono text-xs text-dm-text-accent'>{o.command}</span>
              <span className='min-w-0 truncate'>{o.displayText}</span>
              {o.isCorrect === true && (
                <span className='shrink-0 text-[10px] font-semibold uppercase tracking-wider text-emerald-400'>current</span>
              )}
            </label>
          </li>
        ))}
      </ul>
      <div className='mt-3 flex items-center gap-2'>
        <button
          type='button'
          onClick={() => onApply(picked)}
          disabled={busy || picked < 0 || picked === current}
          className='inline-flex items-center gap-1.5 rounded-md bg-emerald-500 px-3 py-1.5 text-xs font-semibold text-black hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-50'
        >
          {busy ? <LoadingSpinner /> : <WrenchScrewdriverIcon className='size-3.5' aria-hidden='true' />}
          Apply fix
        </button>
        <button
          type='button'
          onClick={onCancel}
          disabled={busy}
          className='rounded-md border border-white/10 px-3 py-1.5 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:opacity-60'
        >
          Cancel
        </button>
        <p className='min-w-0 text-[10px] text-dm-text-accent'>
          Everyone re-scores automatically. Works after the stream ends.
        </p>
      </div>
    </div>
  )
}

const FieldLabel = ({ children }: { children: React.ReactNode }): JSX.Element => (
  <span className='mb-1.5 block text-xs font-medium uppercase tracking-wider text-dm-text-accent'>
    {children}
  </span>
)

const DEFAULT_HEADING_TEXT_COLOR = '#FFFFFF'
const DEFAULT_HEADING_BG = 'linear-gradient(135deg, #1E3A8A 0%, #4338CA 100%)'
const DEFAULT_OPTIONS_TEXT_COLOR = '#FFFFFF'
const DEFAULT_OPTIONS_BG = 'linear-gradient(180deg, #2563EB 0%, #1D4ED8 100%)'
const DEFAULT_CTA_TEXT_COLOR = '#FFFFFF'
const DEFAULT_CTA_BG = 'linear-gradient(180deg, #1E1B4B 0%, #0F172A 100%)'
const DEFAULT_TRIVIA_CTA_TEXT = 'Type #A, #B, #C, or #D in chat to answer'
const MIN_GRACE_SECONDS = 0
const MAX_GRACE_SECONDS = 60
const DEFAULT_OPTION_TEXT_COLOR = '#FFFFFF'
const DEFAULT_OPTION_PROGRESS_COLOR = '#67E8F9'
const DEFAULT_CORNER_RADIUS = 16
const MIN_CORNER_RADIUS = 0
const MAX_CORNER_RADIUS = 48
const DEFAULT_BORDER_WIDTH = 0
const DEFAULT_BORDER_COLOR = '#00000000'
const MIN_BORDER_WIDTH = 0
const MAX_BORDER_WIDTH = 24
const DEFAULT_WINNER_OUTLINE_COLOR = '#67E8F9'

const DEFAULT_OPTIONS: Array<{ displayText: string, command: string }> = [
  { displayText: 'Option A', command: '#A' },
  { displayText: 'Option B', command: '#B' }
]

const emptyTriviaDraft = (): TriviaDraft => ({
  name: '',
  question: '',
  callToActionText: DEFAULT_TRIVIA_CTA_TEXT,
  options: DEFAULT_OPTIONS.map((o) => ({
    ...o,
    textColor: DEFAULT_OPTION_TEXT_COLOR,
    progressBarColor: DEFAULT_OPTION_PROGRESS_COLOR,
    backgroundColor: ''
  })),
  headingTextColor: DEFAULT_HEADING_TEXT_COLOR,
  headingBackground: DEFAULT_HEADING_BG,
  optionsTextColor: DEFAULT_OPTIONS_TEXT_COLOR,
  optionsBackground: DEFAULT_OPTIONS_BG,
  callToActionTextColor: DEFAULT_CTA_TEXT_COLOR,
  callToActionBackground: DEFAULT_CTA_BG,
  alternatingOptionBackground: DEFAULT_OPTIONS_BG,
  backgroundImage: '',
  backgroundImageFit: 'COVER',
  cornerRadius: DEFAULT_CORNER_RADIUS,
  borderWidth: DEFAULT_BORDER_WIDTH,
  borderColor: DEFAULT_BORDER_COLOR,
  winnerOutlineColor: DEFAULT_WINNER_OUTLINE_COLOR,
  fontKey: 'DEFAULT',
  hideLiveResults: false,
  showAnswersCounter: true,
  closeGraceSeconds: DEFAULT_TRIVIA_GRACE_SECONDS,
  correctOptionIndex: -1
})

const SAMPLE_WINNER_SPREAD = [64, 27, 9, 4, 2, 1, 1, 1]
const sampleTriviaLive = (draft: TriviaDraft, stage: 'open-hidden' | 'locked' | 'revealed'): TriviaLiveData => {
  const optionCount = Math.max(draft.options.length, 1)
  const correctIndex = draft.correctOptionIndex >= 0 && draft.correctOptionIndex < optionCount
    ? draft.correctOptionIndex
    : 0
  const counts = SAMPLE_WINNER_SPREAD.slice(0, optionCount)
  if (correctIndex > 0) {
    const tmp = counts[0]
    counts[0] = counts[correctIndex]
    counts[correctIndex] = tmp
  }
  const total = counts.reduce((sum, c) => sum + c, 0)
  const hidden = stage !== 'revealed' && draft.hideLiveResults
  return {
    totalVotes: total,
    optionVoteCounts: counts,
    optionVoteCountsByPlatform: counts.map((c) => ({ twitch: c, youtube: 0 })),
    closed: stage !== 'open-hidden',
    revealed: stage === 'revealed',
    hidden,
    answersIn: hidden && draft.showAnswersCounter ? { twitch: total, youtube: 0 } : null,
    correctIndex: stage === 'revealed' ? correctIndex : -1,
    youtubeRemoved: false
  }
}

type TriviaSizeKey =
  | 'questionFontSize'
  | 'displayTextFontSize'
  | 'commandFontSize'
  | 'numberFontSize'
  | 'callToActionFontSize'
  | 'revealTextFontSize'
const TRIVIA_SIZE_KEYS: TriviaSizeKey[] = [
  'questionFontSize',
  'displayTextFontSize',
  'commandFontSize',
  'numberFontSize',
  'callToActionFontSize',
  'revealTextFontSize'
]

const TriviaPreviewBody = ({ draft, existing, readOnly, elementHeight }: {
  draft: TriviaDraft
  existing: TriviaSummary | null
  readOnly: boolean
  elementHeight?: number
}): JSX.Element => {
  if (readOnly && existing !== null) {
    return (
      <div>
        <div className='mb-1 text-[10px] font-semibold uppercase tracking-wider text-dm-text-accent'>
          Last state
        </div>
        <ScaledStage mode='preview' showCheckerboard elementWidth={POLL.width} elementHeight={elementHeight}>
          <TriviaPreview draft={draft} live={triviaLiveFromSummary(existing)} />
        </ScaledStage>
      </div>
    )
  }
  return (
    <div className='space-y-3'>
      <div>
        <div className='mb-1 text-[10px] font-semibold uppercase tracking-wider text-dm-text-accent'>
          {draft.hideLiveResults ? 'Open (results hidden)' : 'Open (answers coming in)'}
        </div>
        <ScaledStage mode='preview' showCheckerboard elementWidth={POLL.width} elementHeight={elementHeight}>
          {draft.hideLiveResults
            ? <TriviaPreview draft={draft} live={sampleTriviaLive(draft, 'open-hidden')} />
            : <TriviaPreview draft={draft} />}
        </ScaledStage>
      </div>
      <div>
        <div className='mb-1 text-[10px] font-semibold uppercase tracking-wider text-dm-text-accent'>
          Answers locked (after Close)
        </div>
        <ScaledStage mode='preview' showCheckerboard elementWidth={POLL.width} elementHeight={elementHeight}>
          <TriviaPreview draft={draft} live={sampleTriviaLive(draft, 'locked')} />
        </ScaledStage>
      </div>
      <div>
        <div className='mb-1 text-[10px] font-semibold uppercase tracking-wider text-dm-text-accent'>
          Revealed (correct answer shown)
        </div>
        <ScaledStage mode='preview' showCheckerboard elementWidth={POLL.width} elementHeight={elementHeight}>
          <TriviaPreview draft={draft} live={sampleTriviaLive(draft, 'revealed')} />
        </ScaledStage>
      </div>
    </div>
  )
}

const TriviaForm = ({
  event,
  existing,
  onSaved,
  onCancel
}: {
  event: EventDetail
  existing: TriviaSummary | null
  onSaved: (trivia: TriviaSummary) => void
  onCancel: () => void
}): JSX.Element => {
  const { auth0Token } = useAuth()
  const { account } = useEntitlements()
  const [draft, setDraft] = useState<TriviaDraft>(() =>
    existing !== null ? draftFromTrivia(existing) : emptyTriviaDraft()
  )
  const [broadcasterUserIds, setBroadcasterUserIds] = useState<string[]>(() =>
    existing !== null ? existing.broadcasterUserIds : event.channels.map((c) => c.broadcasterUserId)
  )
  const youtubeInvited = account?.youtubeChatInvited === true
  const showYouTubeOptIn = youtubeInvited || eventHasYouTubeSource(event)
  const [youtubeVotesEnabled, setYoutubeVotesEnabled] = useState<boolean>(() =>
    existing !== null ? existing.youtubeVotesEnabled === true : false
  )
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedNote, setSavedNote] = useState(false)
  useEffect(() => {
    setSavedNote(false)
  }, [draft, broadcasterUserIds, youtubeVotesEnabled])

  const isEdit = existing !== null
  const revealedLocked = existing?.revealedAt != null
  const readOnly = event.status === 'ENDED' || revealedLocked
  const canAddOption = draft.options.length < 6
  const canRemoveOption = draft.options.length > 2

  const sizeDefaults = triviaTextSizeDefaults()
  const setSize = (key: TriviaSizeKey) => (next: number | null): void => {
    setDraft((d) => ({ ...d, [key]: next }))
  }
  const [previewRef, previewFit] = usePreviewFit('trivia-preview')
  const anySizeSet = TRIVIA_SIZE_KEYS.some((k) => draft[k] != null)
  const previewHeight = anySizeSet && previewFit.frameHeight > 720
    ? Math.ceil(previewFit.frameHeight)
    : undefined

  const updateOption = (idx: number, patch: Partial<TriviaDraft['options'][number]>): void => {
    setDraft((d) => ({
      ...d,
      options: d.options.map((o, i) => i === idx ? { ...o, ...patch } : o)
    }))
  }

  const addOption = (): void => {
    if (!canAddOption) return
    const letter = String.fromCharCode(65 + draft.options.length)
    setDraft((d) => ({
      ...d,
      options: [
        ...d.options,
        {
          displayText: `Option ${letter}`,
          command: `#${letter}`,
          textColor: DEFAULT_OPTION_TEXT_COLOR,
          progressBarColor: DEFAULT_OPTION_PROGRESS_COLOR,
          backgroundColor: ''
        }
      ]
    }))
  }

  const removeOption = (idx: number): void => {
    if (!canRemoveOption) return
    setDraft((d) => ({
      ...d,
      options: d.options.filter((_, i) => i !== idx),
      correctOptionIndex: d.correctOptionIndex === idx
        ? -1
        : d.correctOptionIndex > idx
          ? d.correctOptionIndex - 1
          : d.correctOptionIndex
    }))
  }

  const toggleChannel = (broadcasterUserId: string): void => {
    setBroadcasterUserIds((prev) => prev.includes(broadcasterUserId)
      ? prev.filter((id) => id !== broadcasterUserId)
      : [...prev, broadcasterUserId])
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault()
    if (auth0Token === undefined || submitting) return
    if (draft.question.trim().length === 0) {
      setError('Question is required.')
      return
    }
    const cmds = draft.options.map((o) => o.command.toLowerCase().trim())
    if (new Set(cmds).size !== cmds.length) {
      setError('Option commands must be unique.')
      return
    }
    if (cmds.some((c) => c.length === 0)) {
      setError('Every option needs a command.')
      return
    }
    if (draft.correctOptionIndex < 0 || draft.correctOptionIndex >= draft.options.length) {
      setError('Pick exactly one correct answer.')
      return
    }

    setSubmitting(true)
    setError(null)
    try {
      const optionsInput: TriviaOptionInput[] = draft.options.map((o, i) => ({
        displayText: o.displayText,
        command: o.command.trim(),
        textColor: o.textColor,
        progressBarColor: o.progressBarColor,
        isCorrect: i === draft.correctOptionIndex
      }))
      const sharedVars = {
        name: draft.name,
        broadcasterUserIds,
        question: draft.question,
        options: optionsInput,
        youtubeVotesEnabled,
        hideLiveResults: draft.hideLiveResults,
        showAnswersCounter: draft.showAnswersCounter,
        closeGraceSeconds: draft.closeGraceSeconds,
        callToActionText: draft.callToActionText.trim().length > 0
          ? draft.callToActionText
          : null,
        headingTextColor: draft.headingTextColor,
        optionsTextColor: draft.optionsTextColor,
        callToActionTextColor: draft.callToActionTextColor,
        headingBackground: draft.headingBackground,
        optionsBackground: draft.optionsBackground,
        callToActionBackground: draft.callToActionBackground,
        alternatingOptionBackground: draft.alternatingOptionBackground === draft.optionsBackground
          ? null
          : draft.alternatingOptionBackground,
        backgroundImage: draft.backgroundImage.trim().length > 0
          ? draft.backgroundImage
          : null,
        backgroundImageFit: draft.backgroundImage.trim().length > 0
          ? draft.backgroundImageFit
          : null,
        cornerRadius: draft.cornerRadius,
        borderWidth: draft.borderWidth,
        borderColor: draft.borderColor,
        winnerOutlineColor: draft.winnerOutlineColor,
        fontKey: draft.fontKey === 'DEFAULT' ? null : draft.fontKey
      }
      const textSizeVars = {
        questionFontSize: draft.questionFontSize ?? null,
        displayTextFontSize: draft.displayTextFontSize ?? null,
        commandFontSize: draft.commandFontSize ?? null,
        numberFontSize: draft.numberFontSize ?? null,
        callToActionFontSize: draft.callToActionFontSize ?? null,
        revealTextFontSize: draft.revealTextFontSize ?? null
      }
      if (isEdit) {
        const { trivia } = await graphqlRequest<{ trivia: TriviaSummary }>(
          updateTriviaMutation,
          { triviaId: existing.triviaId, ...sharedVars, ...textSizeVars },
          auth0Token
        )
        setSavedNote(true)
        onSaved(trivia)
      } else {
        const { trivia } = await graphqlRequest<{ trivia: TriviaSummary }>(
          createTriviaMutation,
          { eventId: event.eventId, ...sharedVars, ...textSizeVars },
          auth0Token
        )
        setSavedNote(true)
        onSaved(trivia)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save trivia question')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form
      onSubmit={(e) => { void handleSubmit(e) }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') {
          e.preventDefault()
        }
      }}
      className='mt-2 grid grid-cols-1 gap-3 px-4 pb-4 lg:grid-cols-3'
    >
      <div className='space-y-1.5 lg:col-span-2'>
        <fieldset disabled={readOnly} className='contents space-y-1.5'>
          <SectionCard
            title={readOnly ? 'View trivia question' : isEdit ? 'Edit trivia question' : 'New trivia question'}
            action={
            readOnly
              ? (
                <span className='inline-flex items-center gap-1.5 rounded-full bg-dm-bg-accent/50 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-dm-text-accent ring-1 ring-inset ring-white/10'>
                  <EyeIcon className='size-3' aria-hidden='true' />
                  Read-only
                </span>
                )
              : (
                <button
                  type='button'
                  onClick={onCancel}
                  className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary'
                >
                  <ArrowLeftIcon className='size-3.5' aria-hidden='true' />
                  Back to trivia
                </button>
                )
          }
          >
            <label className='mb-4 block'>
              <FieldLabel>Name</FieldLabel>
              <input
                value={draft.name}
                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                placeholder='Short label for this trivia question (shown in the list)'
                className='w-full rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
              />
              <p className='mt-1 text-[11px] text-dm-text-accent'>
                Displayed in the trivia list. Defaults to the question text when omitted on create.
              </p>
            </label>
            <label className='mb-4 block'>
              <FieldLabel>Question</FieldLabel>
              <input
                value={draft.question}
                onChange={(e) => setDraft((d) => ({ ...d, question: e.target.value }))}
                placeholder='Which planet has the most moons?'
                required
                className='w-full rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
              />
            </label>
            <label className='mb-4 block'>
              <FieldLabel>Call-to-action text</FieldLabel>
              <input
                value={draft.callToActionText}
                onChange={(e) => setDraft((d) => ({ ...d, callToActionText: e.target.value }))}
                placeholder={DEFAULT_TRIVIA_CTA_TEXT}
                className='w-full rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
              />
              <p className='mt-1 text-[11px] text-dm-text-accent'>
                Leave empty to hide the bottom strip on the overlay.
              </p>
            </label>

            <div className='mb-4'>
              <FieldLabel>
                <span className='flex items-center gap-1.5'>
                  <ProviderBrandIcon src={TWITCH_BRAND_ICON_SRC} size='sm' />
                  Twitch channels
                </span>
              </FieldLabel>
              {event.channels.length === 0
                ? (
                  <p className='rounded-md border border-dashed border-white/10 p-3 text-xs text-dm-text-secondary'>
                    No channels bound to this stream yet. Add one on the Integrations page.
                  </p>
                  )
                : (
                  <ul className='space-y-1.5'>
                    {event.channels.map((c: ChannelBinding) => (
                      <li key={c.broadcasterUserId}>
                        <label className='flex cursor-pointer items-center gap-2.5 rounded-md border border-white/5 bg-dm-bg-accent/30 px-3 py-2 text-sm hover:bg-dm-bg-accent/50'>
                          <input
                            type='checkbox'
                            checked={broadcasterUserIds.includes(c.broadcasterUserId)}
                            onChange={() => toggleChannel(c.broadcasterUserId)}
                            className='size-4 rounded border-white/20 bg-dm-bg-accent/60 text-brand focus:ring-brand'
                          />
                          <ProviderBrandIcon src={TWITCH_BRAND_ICON_SRC} size='sm' />
                          <span className='text-dm-text-primary'>{c.channelLogin}</span>
                        </label>
                      </li>
                    ))}
                  </ul>
                  )}
              {showYouTubeOptIn && (
                <p className='mt-1.5 text-[11px] text-dm-text-accent' data-testid='twitch-channels-optional-hint'>
                  {TWITCH_CHANNELS_OPTIONAL_HINT}
                </p>
              )}
            </div>
            <p className='rounded-md border border-white/5 bg-dm-bg-accent/30 px-3 py-2 text-xs text-dm-text-secondary'>
              A trivia question always counts each viewer once: their first
              answer is final, and a message naming two of this question&apos;s
              commands counts for nothing.
            </p>
            {showYouTubeOptIn && (
              <label className='mt-3 flex items-center gap-2 text-sm text-dm-text-primary'>
                <input
                  type='checkbox'
                  checked={youtubeVotesEnabled}
                  onChange={(e) => setYoutubeVotesEnabled(e.target.checked)}
                  className='size-4 rounded border-white/20 bg-dm-bg-accent/60 text-brand focus:ring-brand'
                />
                Count YouTube votes (this stream&apos;s YouTube sources)
              </label>
            )}
          </SectionCard>
          <SectionCard title='Trivia behavior'>
            <label className='flex items-start gap-2 text-sm text-dm-text-primary'>
              <input
                type='checkbox'
                checked={draft.hideLiveResults}
                onChange={(e) => setDraft((d) => ({ ...d, hideLiveResults: e.target.checked }))}
                disabled={existing?.status === 'ACTIVE'}
                className='mt-0.5 size-4 rounded border-white/20 bg-dm-bg-accent/60 text-brand focus:ring-brand'
              />
              <span>
                Hide live results while the question is open
                <span className='mt-0.5 block text-[11px] text-dm-text-accent'>
                  Viewers see the options without bars or counts until you reveal.
                  You always see the real split on the dashboard. Set before Start;
                  it cannot change while the question is open.
                </span>
              </span>
            </label>
            <label className='mt-3 flex items-start gap-2 text-sm text-dm-text-primary'>
              <input
                type='checkbox'
                checked={draft.showAnswersCounter}
                onChange={(e) => setDraft((d) => ({ ...d, showAnswersCounter: e.target.checked }))}
                className='mt-0.5 size-4 rounded border-white/20 bg-dm-bg-accent/60 text-brand focus:ring-brand'
              />
              <span>
                Show an &quot;answers in&quot; counter while results are hidden
                <span className='mt-0.5 block text-[11px] text-dm-text-accent'>
                  A neutral running count so chat sees participation without the
                  split. Turn it off to hide exact numbers on low-interaction
                  shows. Only applies while results are hidden.
                </span>
              </span>
            </label>
            <label className='mt-4 block'>
              <FieldLabel>Close grace (seconds)</FieldLabel>
              <NumberInput
                value={draft.closeGraceSeconds}
                min={MIN_GRACE_SECONDS}
                max={MAX_GRACE_SECONDS}
                onCommit={(n) => setDraft((d) => ({ ...d, closeGraceSeconds: n }))}
                className='w-24 rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-xs text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
              />
              <p className='mt-1 text-[11px] text-dm-text-accent'>
                Answers still count for this many seconds after you press Close
                (0 to 60), so viewers watching on a stream delay are not cut off.
                The overlay shows &quot;Answers locked&quot; right away.
              </p>
            </label>
          </SectionCard>
          <SectionCard
            title='Options'
            action={
              <button
                type='button'
                onClick={addOption}
                disabled={!canAddOption}
                className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
              >
                <PlusIcon className='size-3.5' aria-hidden='true' />
                Add option
              </button>
          }
          >
            <ul className='space-y-3'>
              {draft.options.map((opt, i) => (
                <li
                  key={i}
                  className={classNames(
                    'rounded-lg border border-white/5 bg-dm-bg-accent/30 p-3',
                    draft.correctOptionIndex === i && 'ring-1 ring-emerald-500/40'
                  )}
                >
                  <div className='mb-2 flex items-center justify-between'>
                    <span className='text-xs font-semibold uppercase tracking-wider text-dm-text-accent'>
                      Option {i + 1}
                    </span>
                    <button
                      type='button'
                      onClick={() => removeOption(i)}
                      disabled={!canRemoveOption}
                      title='Remove this option'
                      className='inline-flex items-center gap-1 rounded-md border border-white/10 px-2 py-0.5 text-[11px] text-dm-text-secondary hover:border-red-500/40 hover:text-red-300 disabled:cursor-not-allowed disabled:opacity-60'
                    >
                      <TrashIcon className='size-3' aria-hidden='true' />
                      Remove
                    </button>
                  </div>
                  <div className='grid grid-cols-2 gap-3'>
                    <label className='block'>
                      <FieldLabel>Display text</FieldLabel>
                      <input
                        value={opt.displayText}
                        onChange={(e) => updateOption(i, { displayText: e.target.value })}
                        required
                        className='w-full rounded-md border border-white/10 bg-dm-bg-secondary px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
                      />
                    </label>
                    <label className='block'>
                      <FieldLabel>Chat command</FieldLabel>
                      <input
                        value={opt.command}
                        onChange={(e) => updateOption(i, { command: e.target.value })}
                        required
                        className='w-full rounded-md border border-white/10 bg-dm-bg-secondary px-3 py-2 font-mono text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
                      />
                    </label>
                    <ColorInput
                      label='Text color'
                      value={opt.textColor}
                      onChange={(v) => updateOption(i, { textColor: v })}
                    />
                    <ColorInput
                      label='Progress bar color'
                      value={opt.progressBarColor}
                      onChange={(v) => updateOption(i, { progressBarColor: v })}
                    />
                  </div>
                  <label className='mt-3 flex items-center gap-2 text-sm text-emerald-300'>
                    <input
                      type='radio'
                      name='trivia-correct-option'
                      checked={draft.correctOptionIndex === i}
                      onChange={() => setDraft((d) => ({ ...d, correctOptionIndex: i }))}
                      className='size-4 border-white/20 bg-dm-bg-accent/60 text-emerald-500 focus:ring-emerald-500'
                    />
                    This is the correct answer
                  </label>
                </li>
              ))}
            </ul>
          </SectionCard>
          <SectionCard title='Font'>
            <label className='block'>
              <FieldLabel>Font</FieldLabel>
              <FontSelect
                value={draft.fontKey}
                onChange={(next) => setDraft((d) => ({ ...d, fontKey: next as ChatFontKey }))}
                defaultLabel='Default (standard element font)'
                className='w-full rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
              />
            </label>
          </SectionCard>
          <SectionCard title='Text sizes'>
            <p className='mb-3 text-[11px] text-dm-text-accent'>
              Pixel sizes on the 1920 x 1080 stream canvas at 100% placement.
              Leave a box empty to use the default shown in it. Resizing the
              question in the Overlay Editor scales these too.
            </p>
            <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
              <TextSizeInput
                label='Question'
                testId='size-question'
                value={draft.questionFontSize ?? null}
                defaultPx={sizeDefaults.question}
                min={TEXT_SIZE_MIN_PX}
                max={TEXT_SIZE_MAX_PX}
                onChange={setSize('questionFontSize')}
              />
              <TextSizeInput
                label='Display text'
                testId='size-display-text'
                value={draft.displayTextFontSize ?? null}
                defaultPx={sizeDefaults.displayText}
                min={TEXT_SIZE_MIN_PX}
                max={TEXT_SIZE_MAX_PX}
                onChange={setSize('displayTextFontSize')}
              />
              <TextSizeInput
                label='Chat command'
                testId='size-command'
                value={draft.commandFontSize ?? null}
                defaultPx={sizeDefaults.command}
                min={TEXT_SIZE_MIN_PX}
                max={TEXT_SIZE_MAX_PX}
                onChange={setSize('commandFontSize')}
              />
              <TextSizeInput
                label='Number (percent or answers)'
                testId='size-number'
                value={draft.numberFontSize ?? null}
                defaultPx={sizeDefaults.number}
                min={TEXT_SIZE_MIN_PX}
                max={TEXT_SIZE_MAX_PX}
                onChange={setSize('numberFontSize')}
              />
              <TextSizeInput
                label='Call to action'
                testId='size-call-to-action'
                value={draft.callToActionFontSize ?? null}
                defaultPx={sizeDefaults.callToAction}
                min={TEXT_SIZE_MIN_PX}
                max={TEXT_SIZE_MAX_PX}
                onChange={setSize('callToActionFontSize')}
              />
              <TextSizeInput
                label='Answer reveal text'
                testId='size-reveal-text'
                value={draft.revealTextFontSize ?? null}
                defaultPx={sizeDefaults.revealText}
                min={TEXT_SIZE_MIN_PX}
                max={TEXT_SIZE_MAX_PX}
                onChange={setSize('revealTextFontSize')}
                help='The "Answers locked" and "Correct answer" labels at the bottom.'
              />
            </div>
            {previewFit.overflows && (
              <p data-testid='text-fit-hint' className='mt-3 text-[11px] text-amber-300'>
                Some text is wider than its box at these sizes and gets cut off. Try a smaller size.
              </p>
            )}
          </SectionCard>

          <SectionCard title='Heading style'>
            <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
              <ColorInput
                label='Heading text color'
                value={draft.headingTextColor}
                onChange={(v) => setDraft((d) => ({ ...d, headingTextColor: v }))}
              />
              <BackgroundInput
                label='Heading background'
                value={draft.headingBackground}
                onChange={(v) => setDraft((d) => ({ ...d, headingBackground: v }))}
              />
            </div>
          </SectionCard>

          <SectionCard title='Options section style'>
            <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
              <BackgroundInput
                label='Odd row background (1st, 3rd, 5th…)'
                value={draft.optionsBackground}
                onChange={(v) => setDraft((d) => ({ ...d, optionsBackground: v }))}
              />
              <BackgroundInput
                label='Even row background (2nd, 4th, 6th…)'
                value={draft.alternatingOptionBackground}
                onChange={(v) => setDraft((d) => ({ ...d, alternatingOptionBackground: v }))}
              />
            </div>
            <p className='mt-3 text-[11px] text-dm-text-accent'>
              Each option&apos;s text + chat command color is set on its row above. Set the
              two backgrounds to the same value for a flat look, or different values for a
              striped one.
            </p>
          </SectionCard>

          <SectionCard title='Container shape'>
            <label className='block'>
              <FieldLabel>Corner radius (px)</FieldLabel>
              <div className='flex items-center gap-2'>
                <input
                  type='range'
                  min={MIN_CORNER_RADIUS}
                  max={MAX_CORNER_RADIUS}
                  value={draft.cornerRadius}
                  onChange={(e) => setDraft((d) => ({ ...d, cornerRadius: Number.parseInt(e.target.value, 10) }))}
                  className='flex-1 accent-brand'
                />
                <NumberInput
                  value={draft.cornerRadius}
                  min={MIN_CORNER_RADIUS}
                  max={MAX_CORNER_RADIUS}
                  onCommit={(n) => setDraft((d) => ({ ...d, cornerRadius: n }))}
                  className='w-20 rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-xs text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
                />
              </div>
              <p className='mt-1 text-[11px] text-dm-text-accent'>
                0 = square corners, higher = more rounded.
              </p>
            </label>
            <label className='mt-4 block'>
              <FieldLabel>Border width (px)</FieldLabel>
              <NumberInput
                value={draft.borderWidth}
                min={MIN_BORDER_WIDTH}
                max={MAX_BORDER_WIDTH}
                onCommit={(n) => setDraft((d) => ({ ...d, borderWidth: n }))}
                className='w-24 rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-xs text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
              />
            </label>
            <div className='mt-4'>
              <ColorInput
                label='Border color'
                value={draft.borderColor}
                onChange={(v) => setDraft((d) => ({ ...d, borderColor: v }))}
              />
            </div>
            <div className='mt-4'>
              <ColorInput
                label='Correct answer highlight'
                value={draft.winnerOutlineColor}
                onChange={(v) => setDraft((d) => ({ ...d, winnerOutlineColor: v }))}
              />
              <p className='mt-1 text-[11px] text-dm-text-accent'>
                The glowing animated outline drawn around the correct answer when you reveal it.
              </p>
            </div>
          </SectionCard>

          <SectionCard title='Overlay background image'>
            <BackgroundImageInput
              url={draft.backgroundImage}
              fit={draft.backgroundImageFit}
              onChangeUrl={(v) => setDraft((d) => ({ ...d, backgroundImage: v }))}
              onChangeFit={(v) => setDraft((d) => ({ ...d, backgroundImageFit: v }))}
              kind='POLL_BACKGROUND'
              label='Background image (optional)'
            />
          </SectionCard>

          <SectionCard title='Call-to-action style'>
            <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
              <ColorInput
                label='Call-to-action text color'
                value={draft.callToActionTextColor}
                onChange={(v) => setDraft((d) => ({ ...d, callToActionTextColor: v }))}
              />
              <BackgroundInput
                label='Call-to-action background'
                value={draft.callToActionBackground}
                onChange={(v) => setDraft((d) => ({ ...d, callToActionBackground: v }))}
              />
            </div>
          </SectionCard>

        </fieldset>
      </div>
      <div className='lg:col-span-1'>
        <div className='sticky top-4 space-y-3'>
          {readOnly
            ? (
              <>
                <div className='rounded-md border border-white/10 bg-dm-bg-accent/30 px-3 py-2 text-[11px] leading-relaxed text-dm-text-secondary'>
                  {revealedLocked
                    ? 'A revealed question cannot be edited. Reset it on the Trivia page to edit and run it again (its answers are deleted and it leaves the leaderboard), or duplicate it to keep this round\'s results; use Fix answer if the wrong option was marked correct.'
                    : 'This stream has ended, so its trivia questions are read-only here. Reveal and the answer fix stay available on the Trivia page.'}
                </div>
                <button
                  type='button'
                  onClick={onCancel}
                  className='inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary'
                >
                  <ArrowLeftIcon className='size-3.5' aria-hidden='true' />
                  Back to trivia
                </button>
              </>
              )
            : (
              <div className='flex items-center gap-2'>
                <button
                  type='submit'
                  disabled={submitting}
                  className='inline-flex flex-1 items-center justify-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-black hover:bg-brand/80 disabled:cursor-not-allowed disabled:opacity-60'
                >
                  {submitting && <LoadingSpinner />}
                  {isEdit ? 'Save changes' : 'Create question'}
                </button>
                <button
                  type='button'
                  onClick={onCancel}
                  disabled={submitting}
                  className='inline-flex flex-1 items-center justify-center rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:opacity-60'
                >
                  Cancel
                </button>
              </div>
              )}
          {error !== null && <ErrorInline onDismiss={() => setError(null)}>{error}</ErrorInline>}
          {savedNote && <SavedNote testId='trivia-saved' />}
          <h4 className='text-xs font-semibold uppercase tracking-wider text-dm-text-accent'>
            Preview
          </h4>
          <div ref={previewRef}>
            <TriviaPreviewBody draft={draft} existing={existing} readOnly={readOnly} elementHeight={previewHeight} />
          </div>
          <p className='text-[11px] text-dm-text-accent'>
            {readOnly
              ? 'Final overlay rendering applies a fixed font + extra spacing uniformly across all trivia questions.'
              : 'Final overlay rendering will have a fixed font + extra spacing applied uniformly. The correct answer never shows on the overlay until you reveal it.'}
          </p>
        </div>
      </div>
    </form>
  )
}

type RowAction = 'delete' | 'duplicate' | 'close' | 'cancel' | 'reveal' | 'reset' | 'fix'

const TriviaListRow = ({ trivia, nowMs, isEnded, busyAction, fixOpen, onEdit, onDuplicate, onDelete, onClose, onCancel, onReveal, onRevealNow, onReset, onToggleFix, onApplyFix }: {
  trivia: TriviaSummary
  nowMs: number
  isEnded: boolean
  busyAction: RowAction | null
  fixOpen: boolean
  onEdit: () => void
  onDuplicate: () => void
  onDelete: () => void
  onClose: () => void
  onCancel: () => void
  onReveal: () => void
  onRevealNow: () => void
  onReset: () => void
  onToggleFix: () => void
  onApplyFix: (index: number) => void
}): JSX.Element => {
  const phase = triviaPhaseOf(trivia, nowMs)
  const revealed = trivia.revealedAt != null
  const isActive = trivia.status === 'ACTIVE'
  const twitchAnswers = trivia.options.reduce((s, o) => s + (o.voteCountByPlatform?.twitch ?? o.voteCount), 0)
  const youtubeAnswers = trivia.options.reduce((s, o) => s + (o.voteCountByPlatform?.youtube ?? 0), 0)
  const graceLeftS = phase === 'grace' && trivia.graceEndsAt != null
    ? Math.max(0, Math.ceil((Date.parse(trivia.graceEndsAt) - nowMs) / 1000))
    : null
  const canClose = !isEnded && phase === 'live'
  const canCancel = !isEnded && isActive && !revealed
  const canReveal = triviaRevealArmed(trivia, nowMs)
  const counting = phase === 'counting'
  const revealNowOffered = triviaRevealNowOffered(trivia, nowMs)
  const stillCountingAfterReveal = revealed && triviaYouTubeCounting(trivia, nowMs)
  const readOnlyRow = isEnded || revealed
  return (
    <li
      data-testid={`trivia-row-${trivia.triviaId}`}
      className='rounded-lg border border-white/5 bg-dm-bg-accent/30 px-4 py-3'
    >
      <div className='flex items-center justify-between gap-3'>
        <div className='flex min-w-0 items-center gap-3'>

          <span className='flex max-w-[12rem] shrink-0 flex-col items-start gap-1'>
            <TriviaStatusChip phase={phase} youtubeTimedOut={triviaYouTubeTimedOut(trivia, nowMs)} />
            {stillCountingAfterReveal && (
              <span
                data-testid={`trivia-still-counting-${trivia.triviaId}`}
                title={TRIVIA_STILL_COUNTING_TOOLTIP}
                className='rounded-full bg-cyan-500/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-cyan-300 ring-1 ring-inset ring-cyan-400/30'
              >
                {TRIVIA_STILL_COUNTING_LABEL}
              </span>
            )}
          </span>
          <div className='flex min-w-0 flex-col leading-tight'>
            <span className='truncate text-sm font-medium text-dm-text-primary' title={trivia.question}>
              {elementDisplayName(trivia.name, trivia.question)}
            </span>
            <span className='truncate text-[11px] text-dm-text-accent'>
              {trivia.options.length} options · Twitch {twitchAnswers} answer{twitchAnswers === 1 ? '' : 's'}
              {youtubeAnswers > 0 ? ` · YouTube ${youtubeAnswers} answer${youtubeAnswers === 1 ? '' : 's'}` : ''}
              {trivia.hideLiveResults === true && !revealed ? ' · results hidden' : ''}
              {graceLeftS !== null ? ` · locks in ${graceLeftS}s` : ''}
              {counting ? ' · counting YouTube' : ''}
            </span>
          </div>
        </div>
        <div className='flex shrink-0 items-center gap-1.5'>
          {!isEnded && (
            <button
              type='button'
              onClick={onClose}
              disabled={busyAction !== null || !canClose}
              title={canClose ? 'Stop taking answers (grace window applies)' : 'Close is available while the question is live'}
              className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
            >
              {busyAction === 'close' ? <LoadingSpinner /> : <LockClosedIcon className='size-3.5' aria-hidden='true' />}
              Close
            </button>
          )}
          {!isEnded && (
            <button
              type='button'
              onClick={onCancel}
              disabled={busyAction !== null || !canCancel}
              title={canCancel
                ? 'Cancel this run: back to a startable draft, answers kept'
                : 'Cancel is available while the question is live, in grace or locked'}
              data-testid={`trivia-cancel-${trivia.triviaId}`}
              className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
            >
              {busyAction === 'cancel' ? <LoadingSpinner /> : <XCircleIcon className='size-3.5' aria-hidden='true' />}
              Cancel
            </button>
          )}
          {counting
            ? (
              <button
                type='button'
                disabled
                title={triviaCountingTooltip(trivia, nowMs)}
                data-testid={`trivia-reveal-${trivia.triviaId}`}
                className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1 text-xs text-dm-text-secondary disabled:cursor-not-allowed disabled:opacity-60'
              >
                <LoadingSpinner />
                {triviaCountingButtonLabel(trivia, nowMs)}
              </button>
              )
            : (
              <button
                type='button'
                onClick={onReveal}
                disabled={busyAction != null || !canReveal}
                title={revealed
                  ? 'Already revealed'
                  : canReveal
                    ? 'Show the correct answer (works after the stream ends)'
                    : 'Reveal arms once the question has run and been closed, its grace window has ended and any YouTube answers are counted'}
                data-testid={`trivia-reveal-${trivia.triviaId}`}
                className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
              >
                {busyAction === 'reveal' ? <LoadingSpinner /> : <SparklesIcon className='size-3.5' aria-hidden='true' />}
                Reveal
              </button>
              )}
          {revealNowOffered && (
            <button
              type='button'
              onClick={onRevealNow}
              disabled={busyAction != null}
              title={TRIVIA_REVEAL_NOW_TOOLTIP}
              data-testid={`trivia-reveal-now-${trivia.triviaId}`}
              className='inline-flex items-center gap-1.5 rounded-md border border-brand/40 px-2.5 py-1 text-xs font-semibold text-brand hover:border-brand/70 hover:bg-brand/10 disabled:cursor-not-allowed disabled:opacity-60'
            >
              {busyAction === 'reveal' ? <LoadingSpinner /> : <SparklesIcon className='size-3.5' aria-hidden='true' />}
              Reveal now
            </button>
          )}

          {!isEnded && (
            <button
              type='button'
              onClick={onReset}
              disabled={busyAction !== null}
              title={revealed
                ? 'Delete every answer, remove it from the leaderboard and return it to a startable draft'
                : 'Delete every recorded answer and start the count over'}
              data-testid={`trivia-reset-${trivia.triviaId}`}
              className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1 text-xs text-dm-text-secondary hover:border-red-500/40 hover:bg-red-500/10 hover:text-red-300 disabled:cursor-not-allowed disabled:opacity-60'
            >
              {busyAction === 'reset' ? <LoadingSpinner /> : <ArrowPathIcon className='size-3.5' aria-hidden='true' />}
              Reset
            </button>
          )}
          {revealed && (
            <button
              type='button'
              onClick={onToggleFix}
              disabled={busyAction !== null && busyAction !== 'fix'}
              title='Re-mark the correct answer (works after the stream ends)'
              className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
            >
              <WrenchScrewdriverIcon className='size-3.5' aria-hidden='true' />
              Fix answer
            </button>
          )}
          <button
            type='button'
            onClick={onEdit}
            title={readOnlyRow ? 'View' : 'Edit'}
            disabled={busyAction !== null}
            className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
          >
            {readOnlyRow
              ? <EyeIcon className='size-3.5' aria-hidden='true' />
              : <PencilSquareIcon className='size-3.5' aria-hidden='true' />}
            {readOnlyRow ? 'View' : 'Edit'}
          </button>
          {!isEnded && (
            <button
              type='button'
              onClick={onDuplicate}
              title='Duplicate (the copy starts fresh and can run again)'
              disabled={busyAction !== null}
              className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
            >
              {busyAction === 'duplicate' ? <LoadingSpinner /> : <DocumentDuplicateIcon className='size-3.5' aria-hidden='true' />}
              Duplicate
            </button>
          )}
          <button
            type='button'
            onClick={onDelete}
            disabled={busyAction !== null || isActive}
            title={isActive ? 'Cancel or reveal this question first' : 'Delete'}
            className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1 text-xs text-dm-text-secondary hover:border-red-500/40 hover:bg-red-500/10 hover:text-red-300 disabled:cursor-not-allowed disabled:opacity-60'
          >
            {busyAction === 'delete' ? <LoadingSpinner /> : <TrashIcon className='size-3.5' aria-hidden='true' />}
            Delete
          </button>
        </div>
      </div>
      {fixOpen && revealed && (
        <FixAnswerPanel
          trivia={trivia}
          busy={busyAction === 'fix'}
          onApply={onApplyFix}
          onCancel={onToggleFix}
        />
      )}
    </li>
  )
}

type Mode =
  | { kind: 'list' }
  | { kind: 'create' }
  | { kind: 'edit', triviaId: string }

export const TriviaPage = (): JSX.Element => {
  const { auth0Token } = useAuth()
  const { socketToken } = useSocketToken()
  const { event, setEvent, refetchEvent } = useEventDetail()
  const isEnded = event.status === 'ENDED'
  useTriviaAdminFold(event.slug, socketToken ?? null, setEvent)
  const [mode, setMode] = useState<Mode>({ kind: 'list' })
  const [rowBusy, setRowBusy] = useState<{ triviaId: string, action: RowAction } | null>(null)
  const [rowError, setRowError] = useState<string | null>(null)
  const [fixOpenFor, setFixOpenFor] = useState<string | null>(null)
  const [reloadSignal, setReloadSignal] = useState(0)

  const questions = event.trivias
  const nowMs = useTriviaGraceTick(questions)

  const [revealNowFor, setRevealNowFor] = useState<string | null>(null)
  const revealNowTarget = revealNowFor == null
    ? null
    : questions.find((t) => t.triviaId === revealNowFor) ?? null
  const revealNowOpen = revealNowTarget != null && triviaPhaseOf(revealNowTarget, nowMs) === 'counting'
  useEffect(() => {
    if (revealNowFor != null && !revealNowOpen) setRevealNowFor(null)
  }, [revealNowFor, revealNowOpen])

  const editing = useMemo<TriviaSummary | null>(() => {
    if (mode.kind !== 'edit') return null
    return questions.find((t) => t.triviaId === mode.triviaId) ?? null
  }, [mode, questions])

  const upsertTrivia = (trivia: TriviaSummary): void => {
    setEvent((prev) => {
      if (prev === null) return prev
      const idx = prev.trivias.findIndex((t) => t.triviaId === trivia.triviaId)
      if (idx === -1) return { ...prev, trivias: [...prev.trivias, trivia] }
      if (prev.trivias[idx].version > trivia.version) return prev
      const next = prev.trivias.slice()
      next[idx] = trivia
      return { ...prev, trivias: next }
    })
  }

  const removeTrivia = (triviaId: string): void => {
    setEvent((prev) => prev === null
      ? prev
      : ({ ...prev, trivias: prev.trivias.filter((t) => t.triviaId !== triviaId) }))
  }

  const runRowAction = async (
    trivia: TriviaSummary,
    action: RowAction,
    fn: (token: string) => Promise<void>
  ): Promise<void> => {
    if (auth0Token === undefined || rowBusy !== null) return
    setRowBusy({ triviaId: trivia.triviaId, action })
    setRowError(null)
    try {
      await fn(auth0Token)
    } catch (err) {
      setRowError(err instanceof Error ? err.message : 'The action failed')
    } finally {
      setRowBusy(null)
    }
  }

  const handleClose = async (trivia: TriviaSummary): Promise<void> => {
    await runRowAction(trivia, 'close', async (token) => {
      const { trivia: updated } = await graphqlRequest<{ trivia: TriviaSummary }>(
        closeTriviaMutation, { triviaId: trivia.triviaId }, token
      )
      upsertTrivia(updated)
    })
  }

  const handleReveal = async (trivia: TriviaSummary, force = false): Promise<void> => {
    await runRowAction(trivia, 'reveal', async (token) => {
      const { trivia: updated } = await graphqlRequest<{ trivia: TriviaSummary }>(
        revealTriviaMutation,
        force ? { triviaId: trivia.triviaId, force: true } : { triviaId: trivia.triviaId },
        token
      )
      upsertTrivia(updated)
      setReloadSignal((s) => s + 1)
    })
  }

  const confirmRevealNow = (): void => {
    const target = revealNowTarget
    setRevealNowFor(null)
    if (target == null) return
    void handleReveal(target, triviaYouTubeCounting(target, Date.now()))
  }

  const handleFix = async (trivia: TriviaSummary, index: number): Promise<void> => {
    await runRowAction(trivia, 'fix', async (token) => {
      const { trivia: updated } = await graphqlRequest<{ trivia: TriviaSummary }>(
        revealTriviaMutation, { triviaId: trivia.triviaId, correctOptionIndex: index }, token
      )
      upsertTrivia(updated)
      setFixOpenFor(null)
      setReloadSignal((s) => s + 1)
    })
  }

  const handleCancel = async (trivia: TriviaSummary): Promise<void> => {
    const ok = globalThis.confirm(
      `Cancel "${trivia.question}"?\n\n` +
      'This stops the run and returns the question to a startable draft. ' +
      'Nothing is deleted: answers recorded so far stay stored, their counts stay on the dashboard, and nothing is scored on the leaderboard until you reveal.\n\n' +
      'Heads up: each viewer answers a question once. If you start this question again, viewers who already answered are locked to their original answer; use Reset to clear all answers for a truly fresh run.'
    )
    if (!ok) return
    await runRowAction(trivia, 'cancel', async (token) => {
      const { trivia: updated } = await graphqlRequest<{ trivia: TriviaSummary }>(
        stopTriviaMutation, { triviaId: trivia.triviaId }, token
      )
      upsertTrivia({ ...updated, closedAt: null, graceEndsAt: null })
    })
  }

  const handleReset = async (trivia: TriviaSummary): Promise<void> => {
    if (!globalThis.confirm(triviaResetConfirmCopy(trivia.question, trivia.revealedAt != null))) return
    await runRowAction(trivia, 'reset', async (token) => {
      const { trivia: updated } = await graphqlRequest<{ trivia: TriviaSummary }>(
        resetTriviaMutation, { triviaId: trivia.triviaId, clearDedupe: true }, token
      )
      upsertTrivia(updated)
      setFixOpenFor((cur) => cur === trivia.triviaId ? null : cur)
      setReloadSignal((s) => s + 1)
    })
  }

  const handleDuplicate = async (trivia: TriviaSummary): Promise<void> => {
    await runRowAction(trivia, 'duplicate', async (token) => {
      const { trivia: created } = await graphqlRequest<{ trivia: TriviaSummary }>(
        duplicateTriviaMutation, { triviaId: trivia.triviaId }, token
      )
      upsertTrivia(created)
    })
  }

  const handleDelete = async (trivia: TriviaSummary): Promise<void> => {
    const ok = globalThis.confirm(
      `Delete trivia question "${trivia.question}"? Its answers and any leaderboard points from it are permanently removed.`
    )
    if (!ok) return
    await runRowAction(trivia, 'delete', async (token) => {
      const { result } = await graphqlRequest<{ result: DeletionResult }>(
        deleteTriviaMutation, { triviaId: trivia.triviaId }, token
      )
      if (result.deleted) {
        removeTrivia(trivia.triviaId)
        setReloadSignal((s) => s + 1)
      }
    })
  }

  if (mode.kind === 'create') {
    return (
      <TriviaForm
        event={event}
        existing={null}
        onSaved={(trivia) => { upsertTrivia(trivia); setMode({ kind: 'edit', triviaId: trivia.triviaId }) }}
        onCancel={() => setMode({ kind: 'list' })}
      />
    )
  }

  if (mode.kind === 'edit') {
    if (editing === null) {
      return (
        <div className='mt-4 px-4 pb-4'>
          <ErrorInline>That trivia question no longer exists. Returning to the list…</ErrorInline>
          <button onClick={() => setMode({ kind: 'list' })} className='mt-3 rounded-md border border-white/10 px-3 py-1.5 text-xs text-dm-text-secondary hover:text-dm-text-primary'>
            Back to trivia
          </button>
        </div>
      )
    }
    return (
      <TriviaForm
        event={event}
        existing={editing}
        onSaved={upsertTrivia}
        onCancel={() => setMode({ kind: 'list' })}
      />
    )
  }

  return (
    <div className='mt-2 flex flex-col gap-1.5 px-4 pb-4'>
      <SectionCard
        title='Trivia'
        action={
          <div className='flex items-center gap-2'>
            <button
              type='button'
              onClick={() => { void refetchEvent() }}
              title='Refresh'
              className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1.5 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary'
            >
              <ArrowPathIcon className='size-3.5' aria-hidden='true' />
              Refresh
            </button>
            {!isEnded && (
              <button
                type='button'
                data-testid='add-trivia-button'
                onClick={() => setMode({ kind: 'create' })}
                className='inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-black hover:bg-brand/80'
              >
                <PlusIcon className='size-3.5' aria-hidden='true' />
                Add new question
              </button>
            )}
          </div>
        }
      >
        <p className='mb-3 text-[11px] leading-relaxed text-dm-text-accent'>
          Each question runs like a poll with a secret correct answer: Start it
          from the overlay editor, Close it when time is up (late answers count
          through the grace window), then Reveal. Only revealed questions score.
          On a question that counts YouTube, FlightDeck holds Reveal a few
          seconds more while it counts the last YouTube answers; if that runs
          long, Reveal now skips the wait.
          Reveal and Fix answer keep working after the stream ends. Reset
          deletes a question&apos;s answers and makes it a startable draft again,
          even after Reveal (it then leaves the leaderboard).
        </p>
        {rowError !== null && (
          <div className='mb-3'>
            <ErrorInline onDismiss={() => setRowError(null)}>{rowError}</ErrorInline>
          </div>
        )}
        {questions.length === 0
          ? (
            <div className='flex flex-col items-center gap-3 rounded-lg border border-dashed border-white/10 px-4 py-10 text-center text-sm text-dm-text-secondary'>
              <QuestionMarkCircleIcon className='size-8 text-dm-text-accent' aria-hidden='true' />
              <p>{isEnded ? 'This stream ended with no trivia questions.' : 'No trivia questions yet for this stream.'}</p>
              {!isEnded && (
                <button
                  type='button'
                  onClick={() => setMode({ kind: 'create' })}
                  className='inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-black hover:bg-brand/80'
                >
                  <PlusIcon className='size-3.5' aria-hidden='true' />
                  Create the first one
                </button>
              )}
            </div>
            )
          : (
            <ul className='space-y-2' data-testid='trivia-list'>
              {sortByName(questions, (t) => elementDisplayName(t.name, t.question), (t) => t.triviaId).map((trivia) => (
                <TriviaListRow
                  key={trivia.triviaId}
                  trivia={trivia}
                  nowMs={nowMs}
                  isEnded={isEnded}
                  busyAction={rowBusy?.triviaId === trivia.triviaId ? rowBusy.action : null}
                  fixOpen={fixOpenFor === trivia.triviaId}
                  onEdit={() => setMode({ kind: 'edit', triviaId: trivia.triviaId })}
                  onDuplicate={() => { void handleDuplicate(trivia) }}
                  onDelete={() => { void handleDelete(trivia) }}
                  onClose={() => { void handleClose(trivia) }}
                  onCancel={() => { void handleCancel(trivia) }}
                  onReveal={() => { void handleReveal(trivia) }}
                  onRevealNow={() => setRevealNowFor(trivia.triviaId)}
                  onReset={() => { void handleReset(trivia) }}
                  onToggleFix={() => setFixOpenFor((cur) => cur === trivia.triviaId ? null : trivia.triviaId)}
                  onApplyFix={(index) => { void handleFix(trivia, index) }}
                />
              ))}
            </ul>
            )}
      </SectionCard>
      <TriviaLeaderboardPanel eventId={event.eventId} reloadSignal={reloadSignal} trivias={questions} nowMs={nowMs} />
      {revealNowOpen && (
        <ConfirmDialog
          title={TRIVIA_REVEAL_NOW_CONFIRM.title}
          confirmLabel={TRIVIA_REVEAL_NOW_CONFIRM.confirmLabel}
          cancelLabel={TRIVIA_REVEAL_NOW_CONFIRM.cancelLabel}
          onConfirm={confirmRevealNow}
          onCancel={() => setRevealNowFor(null)}
          testId='trivia-reveal-now-dialog'
        >
          {TRIVIA_REVEAL_NOW_CONFIRM.body}
        </ConfirmDialog>
      )}
    </div>
  )
}
